import React, { useState, useEffect, useRef } from 'react';
import {
  fetchDictionaryWords,
  fetchDictionaryWordAudio,
  fetchUserVocabulary,
  addUserVocabularyWord,
  updateUserVocabularyWord,
  deleteUserVocabularyWord
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';

const emptyForm = { english_word: '', czech_translation: '', example_sentence: '', topic: '' };

const VocabularyDictionary = ({ onBackToLessons, popupMode = false }) => {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [playingWord, setPlayingWord] = useState(null);
  const audioRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('lessons');

  // My words state
  const [myWords, setMyWords] = useState([]);
  const [myWordsLoading, setMyWordsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadWords();
  }, []);

  useEffect(() => {
    if (activeTab === 'my' && myWords.length === 0 && !myWordsLoading) {
      loadMyWords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadWords = async () => {
    try {
      setLoading(true);
      const data = await fetchDictionaryWords();
      setWords(data.words || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching dictionary words:', err);
      setError('Nepodařilo se načíst slovník');
    } finally {
      setLoading(false);
    }
  };

  const loadMyWords = async () => {
    try {
      setMyWordsLoading(true);
      const data = await fetchUserVocabulary();
      setMyWords(data || []);
    } catch (err) {
      console.error('Error fetching user vocabulary:', err);
    } finally {
      setMyWordsLoading(false);
    }
  };

  const handleAddWord = async () => {
    if (!formData.english_word.trim() || !formData.czech_translation.trim()) return;
    try {
      setSaving(true);
      const payload = {
        english_word: formData.english_word.trim(),
        czech_translation: formData.czech_translation.trim(),
        example_sentence: formData.example_sentence.trim() || null,
        topic: formData.topic.trim() || null,
      };
      const newWord = await addUserVocabularyWord(payload);
      setMyWords(prev => [...prev, newWord]);
      setFormData(emptyForm);
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding word:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWord = async (id) => {
    if (!editData.english_word.trim() || !editData.czech_translation.trim()) return;
    try {
      setSaving(true);
      const payload = {
        english_word: editData.english_word.trim(),
        czech_translation: editData.czech_translation.trim(),
        example_sentence: editData.example_sentence.trim() || null,
        topic: editData.topic.trim() || null,
      };
      const updated = await updateUserVocabularyWord(id, payload);
      setMyWords(prev => prev.map(w => w.id === id ? updated : w));
      setEditingId(null);
    } catch (err) {
      console.error('Error updating word:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWord = async (id) => {
    if (!window.confirm('Opravdu chcete smazat toto slovíčko?')) return;
    try {
      await deleteUserVocabularyWord(id);
      setMyWords(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      console.error('Error deleting word:', err);
    }
  };

  const startEditing = (word) => {
    setEditingId(word.id);
    setEditData({
      english_word: word.english_word,
      czech_translation: word.czech_translation,
      example_sentence: word.example_sentence || '',
      topic: word.topic || '',
    });
  };

  const playAudio = async (word) => {
    try {
      setPlayingWord(word);
      const audioBlob = await fetchDictionaryWordAudio(word);
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => {
          setPlayingWord(null);
          URL.revokeObjectURL(audioUrl);
        };
        audioRef.current.onerror = () => {
          setPlayingWord(null);
          URL.revokeObjectURL(audioUrl);
        };
        audioRef.current.play();
      }
    } catch (err) {
      console.error('Error playing audio:', err);
      setPlayingWord(null);
    }
  };

  const highlightWord = (sentence, word) => {
    const regex = new RegExp(`(${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = sentence.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <strong key={index} style={{ fontWeight: 'bold' }}>{part}</strong>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  const filteredWords = words.filter(word =>
    word.english_word.toLowerCase().includes(searchTerm.toLowerCase()) ||
    word.czech_translation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMyWords = myWords.filter(word =>
    word.english_word.toLowerCase().includes(searchTerm.toLowerCase()) ||
    word.czech_translation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentFiltered = activeTab === 'lessons' ? filteredWords : filteredMyWords;

  if (loading) {
    if (popupMode) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status" />
            <p style={{ marginTop: '12px', color: '#666' }}>Načítám slovník...</p>
          </div>
        </div>
      );
    }
    return <Loading />;
  }
  if (error) {
    if (popupMode) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#dc3545' }}>
          <p>{error}</p>
          <button onClick={loadWords} className="btn btn-primary btn-sm mt-2">Zkusit znovu</button>
        </div>
      );
    }
    return <Error message={error} />;
  }

  const tabStyle = (isActive) => ({
    padding: '10px 24px',
    fontSize: '1rem',
    fontWeight: isActive ? '600' : '400',
    color: isActive ? '#007bff' : '#666',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: isActive ? '3px solid #007bff' : '3px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    outline: 'none',
  };

  const renderWordForm = (data, setData, onSubmit, onCancel, submitLabel) => (
    <div style={{
      backgroundColor: '#f0fff0',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '20px',
      border: '1px solid #c3e6cb'
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ fontSize: '0.85rem', color: '#555', marginBottom: '4px', display: 'block' }}>
            Anglicky <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            style={inputStyle}
            value={data.english_word}
            onChange={(e) => setData(prev => ({ ...prev, english_word: e.target.value }))}
            placeholder="dog"
          />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', color: '#555', marginBottom: '4px', display: 'block' }}>
            Česky <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            style={inputStyle}
            value={data.czech_translation}
            onChange={(e) => setData(prev => ({ ...prev, czech_translation: e.target.value }))}
            placeholder="pes"
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <div>
          <label style={{ fontSize: '0.85rem', color: '#555', marginBottom: '4px', display: 'block' }}>
            Příklad věty
          </label>
          <input
            style={inputStyle}
            value={data.example_sentence}
            onChange={(e) => setData(prev => ({ ...prev, example_sentence: e.target.value }))}
            placeholder="I have a dog."
          />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', color: '#555', marginBottom: '4px', display: 'block' }}>
            Téma
          </label>
          <input
            style={inputStyle}
            value={data.topic}
            onChange={(e) => setData(prev => ({ ...prev, topic: e.target.value }))}
            placeholder="animals"
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={onSubmit}
          disabled={saving || !data.english_word.trim() || !data.czech_translation.trim()}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 20px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            opacity: saving || !data.english_word.trim() || !data.czech_translation.trim() ? 0.6 : 1,
          }}
        >
          {saving ? 'Ukládám...' : submitLabel}
        </button>
        <button
          onClick={onCancel}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 20px',
            cursor: 'pointer',
            fontWeight: '500',
          }}
        >
          Zrušit
        </button>
      </div>
    </div>
  );

  const renderMyWordCard = (word) => {
    if (editingId === word.id) {
      return (
        <div key={word.id}>
          {renderWordForm(
            editData,
            setEditData,
            () => handleUpdateWord(word.id),
            () => setEditingId(null),
            'Uložit'
          )}
        </div>
      );
    }

    return (
      <div
        key={word.id}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e8e8e8',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '8px'
        }}>
          <div>
            <h3 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '4px', color: '#1a1a1a' }}>
              {word.english_word}
            </h3>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              {word.czech_translation}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
            <button
              onClick={() => playAudio(word.english_word)}
              disabled={playingWord === word.english_word}
              style={{
                backgroundColor: playingWord === word.english_word ? '#e0e0e0' : '#f0f7ff',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: playingWord === word.english_word ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (playingWord !== word.english_word) {
                  e.currentTarget.style.backgroundColor = '#dbeafe';
                }
              }}
              onMouseLeave={(e) => {
                if (playingWord !== word.english_word) {
                  e.currentTarget.style.backgroundColor = '#f0f7ff';
                }
              }}
              title="Přehrát výslovnost"
            >
              {playingWord === word.english_word ? '...' : '▶'}
            </button>
            <button
              onClick={() => startEditing(word)}
              style={{
                backgroundColor: '#f0f7ff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#007bff',
                fontWeight: '500',
              }}
            >
              Upravit
            </button>
            <button
              onClick={() => handleDeleteWord(word.id)}
              style={{
                backgroundColor: '#fff0f0',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#dc3545',
                fontWeight: '500',
              }}
            >
              Smazat
            </button>
          </div>
        </div>
        {word.topic && (
          <span style={{
            display: 'inline-block',
            backgroundColor: '#e8f4fd',
            color: '#0077b6',
            fontSize: '0.8rem',
            padding: '2px 8px',
            borderRadius: '4px',
            marginBottom: '8px',
          }}>
            {word.topic}
          </span>
        )}
        {word.example_sentence && (
          <p style={{
            fontSize: '0.95rem',
            color: '#444',
            lineHeight: '1.5',
            margin: 0,
            paddingTop: '8px',
            borderTop: '1px solid #f0f0f0',
            fontStyle: 'italic',
          }}>
            {word.example_sentence}
          </p>
        )}
      </div>
    );
  };

  return (
    <div style={{
      padding: popupMode ? '20px 16px' : '40px 20px',
      backgroundColor: '#f5f5f5',
      minHeight: popupMode ? 'auto' : '100vh'
    }}>
      <div style={{ maxWidth: popupMode ? '100%' : '1200px', margin: '0 auto' }}>
        {/* Hidden audio element */}
        <audio ref={audioRef} />

        {/* Back button */}
        {!popupMode && <div style={{ marginBottom: '20px' }}>
          <button
            onClick={onBackToLessons}
            style={{
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '14px 24px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.borderColor = '#ccc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = '#e0e0e0';
            }}
          >
            <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>
              ← Zpět na lekce
            </span>
          </button>
        </div>}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{
            fontSize: popupMode ? '1.5rem' : '2rem',
            fontWeight: '500',
            marginBottom: '8px',
            color: '#1a1a1a'
          }}>
            Slovník
          </h1>
          <p style={{ fontSize: '1rem', color: '#666' }}>
            {currentFiltered.length} slovíček
          </p>
        </div>

        {/* Search bar */}
        <div style={{ marginBottom: '20px', maxWidth: '400px', margin: '0 auto 20px' }}>
          <input
            type="text"
            placeholder="Hledat slovo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '1rem',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#007bff';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e0e0e0';
            }}
          />
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '30px',
          borderBottom: '1px solid #e0e0e0',
        }}>
          <button
            style={tabStyle(activeTab === 'lessons')}
            onClick={() => setActiveTab('lessons')}
          >
            Slovíčka z lekcí
          </button>
          <button
            style={tabStyle(activeTab === 'my')}
            onClick={() => setActiveTab('my')}
          >
            Moje slovíčka
          </button>
        </div>

        {/* Lessons tab */}
        {activeTab === 'lessons' && (
          <>
            {filteredWords.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
                {searchTerm ? 'Žádná slovíčka neodpovídají vyhledávání' : 'Slovník je prázdný'}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: popupMode ? 'repeat(auto-fill, minmax(260px, 1fr))' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '20px'
              }}>
                {filteredWords.map((word, index) => (
                  <div
                    key={`${word.id}-${index}`}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e8e8e8',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <div>
                        <h3 style={{
                          fontSize: '1.4rem',
                          fontWeight: '600',
                          marginBottom: '4px',
                          color: '#1a1a1a'
                        }}>
                          {word.english_word}
                        </h3>
                        <p style={{
                          fontSize: '1rem',
                          color: '#666',
                          margin: 0
                        }}>
                          {word.czech_translation}
                        </p>
                      </div>
                      <button
                        onClick={() => playAudio(word.english_word)}
                        disabled={playingWord === word.english_word}
                        style={{
                          backgroundColor: playingWord === word.english_word ? '#e0e0e0' : '#f0f7ff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '44px',
                          height: '44px',
                          cursor: playingWord === word.english_word ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          if (playingWord !== word.english_word) {
                            e.currentTarget.style.backgroundColor = '#dbeafe';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (playingWord !== word.english_word) {
                            e.currentTarget.style.backgroundColor = '#f0f7ff';
                          }
                        }}
                        title="Přehrát výslovnost"
                      >
                        {playingWord === word.english_word ? '...' : '▶'}
                      </button>
                    </div>
                    <p style={{
                      fontSize: '0.95rem',
                      color: '#444',
                      lineHeight: '1.5',
                      margin: 0,
                      paddingTop: '12px',
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      {highlightWord(word.example_sentence, word.english_word)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* My words tab */}
        {activeTab === 'my' && (
          <>
            {/* Add button */}
            {!showAddForm && (
              <div style={{ marginBottom: '20px' }}>
                <button
                  onClick={() => { setShowAddForm(true); setFormData(emptyForm); }}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '0.95rem',
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  + Přidat slovíčko
                </button>
              </div>
            )}

            {/* Add form */}
            {showAddForm && renderWordForm(
              formData,
              setFormData,
              handleAddWord,
              () => setShowAddForm(false),
              'Přidat'
            )}

            {myWordsLoading ? (
              <Loading />
            ) : filteredMyWords.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
                {searchTerm
                  ? 'Žádná slovíčka neodpovídají vyhledávání'
                  : 'Zatím nemáte žádná vlastní slovíčka'}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: popupMode ? 'repeat(auto-fill, minmax(260px, 1fr))' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '20px'
              }}>
                {filteredMyWords.map(renderMyWordCard)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VocabularyDictionary;
